import React from "react";
import { InlineMath } from "react-katex";

/**
 * CircleTangentChordTwoAnglesDiagram
 * ----------------------------------
 * Łuk AB i dwa/trzy kąty oparte na tym samym łuku:
 *  - kąt wpisany α_w (FIOLET),
 *  - kąt styczna–cięciwa α_t (ZIELONY),
 *  - (opcjonalnie) kąt środkowy α_sr (BORDOWY).
 *
 * Podpisy (α, subskrypty) i literki punktów są POMARAŃCZOWE.
 *
 * Label props:
 * - undefined -> pokaz warto?? w stopniach
 * - "" / null -> pokaz sam symbol alfa z indeksem
 * - "40^{\\circ}" -> pokaz sam? warto?? liczbow?
 */
export default function CircleTangentChordTwoAnglesDiagram({
  centralDeg,
  size = 260,
  circleR = 105,
  showCentral = false,
  labelSr,
  labelW,
  labelT,
  labelOffset = 16,
  padding = 40,
}) {
  const cx = size / 2;
  const cy = size / 2;

  const COLORS = {
    accent: "#f59e0b",
    p1: "#a855f7",
    p2: "#f59e0b",
    sr: "#a855f7",
    base: "currentColor",
  };

  let C = Math.max(5, Math.min(300, Number(centralDeg) || 120)); // α_sr
  const W = C / 2; // α_w = α_t

  const a1 = (-C / 2) * (Math.PI / 180);
  const a2 = (C / 2) * (Math.PI / 180);
  const amid = (a1 + a2) / 2;

  const Ax = cx + circleR * Math.cos(a1);
  const Ay = cy + circleR * Math.sin(a1);
  const Bx = cx + circleR * Math.cos(a2);
  const By = cy + circleR * Math.sin(a2);

  const Vx = cx - circleR;
  const Vy = cy;

  const rad1 = { x: cx + circleR * Math.cos(a1), y: cy + circleR * Math.sin(a1) };
  const rad2 = { x: cx + circleR * Math.cos(a2), y: cy + circleR * Math.sin(a2) };

  const aV1 = Math.atan2(Ay - Vy, Ax - Vx);
  const aV2 = Math.atan2(By - Vy, Bx - Vx);
  const amidInscr = (aV1 + aV2) / 2;

  let dV = ((aV2 - aV1) * 180) / Math.PI;
  while (dV <= -180) dV += 360;
  while (dV > 180) dV -= 360;
  const absDV = Math.abs(dV);

  const aA = Math.atan2(Ay - cy, Ax - cx);
  const aTan = aA + Math.PI / 2;
  const tanLen = size + 2 * padding;
  const Tx1 = Ax - tanLen * Math.cos(aTan);
  const Ty1 = Ay - tanLen * Math.sin(aTan);
  const Tx2 = Ax + tanLen * Math.cos(aTan);
  const Ty2 = Ay + tanLen * Math.sin(aTan);

  const aChordFromA = Math.atan2(By - Ay, Bx - Ax);
  let dT = aChordFromA - aTan;
  while (dT < 0) dT += 2 * Math.PI;
  while (dT > 2 * Math.PI) dT -= 2 * Math.PI;
  if (dT > Math.PI) dT = 2 * Math.PI - dT;

  const centralArcR = 25;
  const inscribedArcR = 25;
  const tangentArcR = 22;

  const pCentralStart = { x: cx + centralArcR * Math.cos(a1), y: cy + centralArcR * Math.sin(a1) };
  const pCentralEnd   = { x: cx + centralArcR * Math.cos(a2), y: cy + centralArcR * Math.sin(a2) };

  const pInscribedStart = { x: Vx + inscribedArcR * Math.cos(aV1), y: Vy + inscribedArcR * Math.sin(aV1) };
  const pInscribedEnd   = { x: Vx + inscribedArcR * Math.cos(aV2), y: Vy + inscribedArcR * Math.sin(aV2) };

  const tStart = aTan;
  const tEnd   = aTan + dT;
  const pTangentStart = { x: Ax + tangentArcR * Math.cos(tStart), y: Ay + tangentArcR * Math.sin(tStart) };
  const pTangentEnd   = { x: Ax + tangentArcR * Math.cos(tEnd),   y: Ay + tangentArcR * Math.sin(tEnd)   };

  const fmt = (x) => Number(x).toFixed(2).replace(/\.?0+$/, "");

  const RIGHT_PAD = 1;

  const centralLabelR = centralArcR + 5;
  const labelCentralX = cx + centralLabelR * Math.cos(amid) + RIGHT_PAD;
  const labelCentralY = cy + centralLabelR * Math.sin(amid);

  const inscribedLabelR = inscribedArcR + 10;
  const labelInscrX = Vx + inscribedLabelR * Math.cos(amidInscr) + RIGHT_PAD;
  const labelInscrY = Vy + inscribedLabelR * Math.sin(amidInscr);

  const amidTangent = (tStart + tEnd) / 2;
  const tangentLabelR = tangentArcR + 10;
  const labelTangentX = Ax + tangentLabelR * Math.cos(amidTangent) + RIGHT_PAD;
  const labelTangentY = Ay + tangentLabelR * Math.sin(amidTangent);

  // --- NOWE: rozróżniamy undefined vs ""/null ---
  const valueOrUndefined = (v, fallback) => {
    if (v === "" || v === null) return undefined; // pokaż samo α_sub
    if (typeof v === "string") return v;          // pokaż "= v"
    return fallback;                               // undefined -> fallback (stopnie)
  };

  const valSr = valueOrUndefined(labelSr, `${fmt(C)}^{\\circ}`);
  const valW  = valueOrUndefined(labelW,  `${fmt(W)}^{\\circ}`);
  const valT  = valueOrUndefined(labelT,  `${fmt(W)}^{\\circ}`);

  const AngleLabel = ({ x, y, sub, value, color = COLORS.accent }) => {
    const math = value ? value : `\\alpha_{${sub}}`;
    return (
      <foreignObject x={x} y={y - 13} width="92" height="28">
        <div style={{ color, fontSize: 14, lineHeight: 1.2 }}>
          <InlineMath math={math} />
        </div>
      </foreignObject>
    );
  };

  const PointLabel = ({ x, y, math, anchor = "middle" }) => {
    const width = 36;
    const boxX = anchor === "start" ? x : anchor === "end" ? x - width : x - width / 2;
    const textAlign = anchor === "start" ? "left" : anchor === "end" ? "right" : "center";
    return (
      <foreignObject x={boxX} y={y - 13} width={width} height="28">
        <div style={{ color: COLORS.base, fontSize: 15, textAlign, lineHeight: 1.2 }}>
          <InlineMath math={math} />
        </div>
      </foreignObject>
    );
  };
  const centralFlags = { large: C > 180 ? 1 : 0, sweep: 1 };
  const inscrFlags   = { large: absDV > 180 ? 1 : 0, sweep: 1 };
  const tangFlags    = { large: dT > Math.PI ? 1 : 0, sweep: 1 };

  const placeOutside = (px, py, offset = labelOffset) => {
    const vx = px - cx;
    const vy = py - cy;
    const len = Math.hypot(vx, vy) || 1;
    const ux = vx / len;
    const uy = vy / len;
    const x = px + offset * ux;
    const y = py + offset * uy;
    let anchor = "middle";
    if (ux > 0.35) anchor = "start";
    else if (ux < -0.35) anchor = "end";
    return { x, y, anchor };
  };

  const Apos = placeOutside(Ax, Ay);
  const Bpos = placeOutside(Bx, By);
  const Ppos = placeOutside(Vx, Vy);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`${-padding} ${-padding} ${size + 2 * padding} ${size + 2 * padding}`}
    >
      {/* okrąg */}
      <circle cx={cx} cy={cy} r={circleR} fill="none" stroke={COLORS.base} strokeWidth="2" />

      {/* cięciwa AB */}
      <line x1={Ax} y1={Ay} x2={Bx} y2={By} stroke={COLORS.base} strokeWidth="2.8" strokeLinecap="round" />

      {/* styczna w A */}
      <line x1={Tx1} y1={Ty1} x2={Tx2} y2={Ty2} stroke={COLORS.p2} strokeWidth="2.6" strokeLinecap="round" />

      {/* kąt środkowy (opcjonalnie) */}
      {showCentral && (
        <>
          <line x1={cx} y1={cy} x2={rad1.x} y2={rad1.y} stroke={COLORS.sr} strokeWidth="2.8" strokeLinecap="round" />
          <line x1={cx} y1={cy} x2={rad2.x} y2={rad2.y} stroke={COLORS.sr} strokeWidth="2.8" strokeLinecap="round" />
          <path
            d={`M ${pCentralStart.x} ${pCentralStart.y}
                A ${centralArcR} ${centralArcR} 0 ${centralFlags.large} ${centralFlags.sweep}
                  ${pCentralEnd.x} ${pCentralEnd.y}`}
            fill="none"
            stroke={COLORS.sr}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <AngleLabel x={labelCentralX} y={labelCentralY} sub="sr" value={valSr} color={COLORS.sr} />
        </>
      )}

      {/* kąt wpisany */}
      <line x1={Vx} y1={Vy} x2={Ax} y2={Ay} stroke={COLORS.p1} strokeWidth="2.8" strokeLinecap="round" />
      <line x1={Vx} y1={Vy} x2={Bx} y2={By} stroke={COLORS.p1} strokeWidth="2.8" strokeLinecap="round" />
      <path
        d={`M ${pInscribedStart.x} ${pInscribedStart.y}
            A ${inscribedArcR} ${inscribedArcR} 0 ${inscrFlags.large} ${inscrFlags.sweep}
              ${pInscribedEnd.x} ${pInscribedEnd.y}`}
        fill="none"
        stroke={COLORS.p1}
        strokeWidth="3"
        strokeLinecap="round"
      />
      <AngleLabel x={labelInscrX} y={labelInscrY} sub="w" value={valW} color={COLORS.p1} />

      {/* kąt styczna–cięciwa */}
      <path
        d={`M ${pTangentStart.x} ${pTangentStart.y}
            A ${tangentArcR} ${tangentArcR} 0 ${tangFlags.large} ${tangFlags.sweep}
              ${pTangentEnd.x} ${pTangentEnd.y}`}
        fill="none"
        stroke={COLORS.p2}
        strokeWidth="3"
        strokeLinecap="round"
      />
      <AngleLabel x={labelTangentX} y={labelTangentY} sub="t" value={valT} color={COLORS.p2} />

      {/* punkty */}
      <circle cx={Ax} cy={Ay} r="4.2" fill={COLORS.base} />
      <circle cx={Bx} cy={By} r="4.2" fill={COLORS.base} />
      <circle cx={Vx} cy={Vy} r="4.2" fill={COLORS.base} />
      <circle cx={cx} cy={cy} r="3.2" fill={COLORS.base} />

      {/* etykiety punktów */}
      <PointLabel x={Apos.x} y={Apos.y} math="A" anchor={Apos.anchor} />
      <PointLabel x={Bpos.x} y={Bpos.y} math="B" anchor={Bpos.anchor} />
      <PointLabel x={Ppos.x} y={Ppos.y} math="P" anchor={Ppos.anchor} />
      <PointLabel x={cx} y={cy - 10} math="O" />
    </svg>
  );
}
